import classNames from 'classnames/bind';
import React from 'react';

import styles from './Loader.module.scss';

/**
 * Style variants enum of the Loader component.
 * @enum
 */
export enum LoaderVariant {
  Spinner = 'spinner',
}

/** Properties interface of the Loader component. */
export interface LoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** React classname of the Loader component. */
  className?: string;

  /**
   * Milliseconds between message rotations; pairs with `messages`.
   * @defaultValue `15000`
   */
  interval?: number;

  /**
   * Screen reader only messages announced while loading; pairs with `interval`.
   * @defaultValue `['Loading...']`
   */
  messages?: string[];

  /**
   * Style variant of the Loader component.
   * @defaultValue `LoaderVariant.Spinner`
   */
  variant?: LoaderVariant;
}

/** Default properties of the Loader component. */
const defaultProps: Partial<LoaderProps> = {
  variant: LoaderVariant.Spinner,
};

/**
 * Classnames binding for the Loader component.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * A Sitecore-ready implementation of the Loader component.
 *
 * @see [Orchid Storybook](https://upmchporchid.azurewebsites.net/storybook/?path=/story/components-loader--spinner) for design references
 * @see [Orchid Design System](https://upmchporchid.azurewebsites.net/components/non-skeleton-loading#usage) for usage details
 *
 * @example
 * ```tsx
 * <Loader variant={LoaderVariant.Spinner} />
 * ```
 *
 * @param props - Loader properties.
 * @returns A JSX element.
 */
const Loader: React.FC<LoaderProps> = (propsWithoutDefaults) => {
  const props = {
    variant: propsWithoutDefaults.variant ?? defaultProps.variant,
    ...propsWithoutDefaults,
  };

  const { className, interval = 15000, messages = ['Loading...'], variant, ...rest } = props;
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (messages.length <= 1) return;

    setIndex(0);
    const id = globalThis.setInterval(() => {
      setIndex((i) => (i + 1) % messages.length);
    }, interval);

    return () => {
      globalThis.clearInterval(id);
    };
  }, [interval, messages.length]);

  /** Combined className string for the underlying container element. */
  const classes: string = cx(
    {
      loader: true,
      [variant as LoaderVariant]: true,
    },
    className
  );

  const currentMessage = messages[index % messages.length];

  return (
    <div className={classes} data-testid="loader" {...rest}>
      <span className={cx({ label: true, 'screen-reader-only': true })} role="status">
        {currentMessage}
      </span>
      <progress aria-hidden="true" className={cx({ progress: true, 'screen-reader-only': true })} />
      <div aria-hidden="true" className={styles.value} />
      <div aria-hidden="true" className={styles.track} />
    </div>
  );
};

Loader.displayName = 'Loader';

export default Loader;
